import _ from 'lodash';
import { batch } from 'react-redux';
import template from 'url-template';
import { v4 as uuidv4 } from 'uuid';
import axios from 'axios';
import queryString from 'query-string';

import {
    BEGIN_FETCH_LINKS,
    BEGIN_FETCH_CONTACT_LINKS,
    CLEAR_CONTACTS,
    CLEAR_ERP_ENTITIES,
    FETCH_AREAS,
    FETCH_BUILDING_TYPES,
    FETCH_BUSINESS_TYPES,
    FETCH_CAMPUS_SITES,
    FETCH_CLIENT_NODES,
    FETCH_CONTACTS,
    FETCH_CONTACT_LINKS,
    FETCH_CONVERSIONS,
    FETCH_CORE_AREAS,
    FETCH_EQUIPMENT,
    FETCH_EQUIPMENT_MANUFACTURERS,
    FETCH_ERP_AGENT_CONFIGURATIONS,
    FETCH_ERP_CLIENTS,
    FETCH_ERP_SITES,
    FETCH_ERP_SERVICE_APPOINTMENT_MAPPING,
    FETCH_ERP_SERVICE_CALL_MAPPING,
    FETCH_ERP_EQUIPMENT,
    FETCH_ERP_CONTACTS,
    FETCH_ERP_STATUSES,
    FETCH_ERP_TECHNICIANS,
    FETCH_FILTERED_AREAS,
    FETCH_FILTERED_EQUIPMENT,
    FETCH_FILTERED_SYSTEMS,
    FETCH_LINKS,
    FETCH_RAW_UNITS,
    FETCH_TECHNICIAN_LIST,
    FETCH_SENSORS,
    FETCH_TECHNICIAN_SENIORITY_LEVELS,
    FETCH_TECHNICIAN_SPECIALTIES,
    FETCH_SERVICE_APPOINTMENT_STATUSES,
    FETCH_SERVICE_CALL_STATUSES,
    FETCH_SYSTEMS,
    FETCH_FUEL_TYPES,
    FETCH_WEATHER_DATA,
    WEATHER_DATA_LOAD_BEGIN,
    WEATHER_DATA_LOAD_END,
    WEATHER_DATA_LOAD_ERROR,
    FETCH_WEATHER_STATION,
    ITEM_REORDER,
    SET_AREA,
    SET_CONTACT,
    SET_CONVERSION,
    SET_EQUIPMENT,
    SET_ERP_AGENT,
    SET_LINK,
    SET_NODE,
    SET_NODE_UPDATE,
    SET_SENSOR,
    SET_SELECTED_TECHNICIAN,
    SET_SYSTEM,
    SET_TREE,
    SET_TREE_RESET,
    SET_TREE_TYPE,
    TECHNICIAN_DATA_LOAD_BEGIN,
    TECHNICIAN_DATA_LOAD_END,
    TECHNICIAN_DATA_LOAD_ERROR,
    TOGGLE_LOADING,
    TOGGLE_CONTACT_LINKS_LOADING,
    TOGGLE_ERP_CLIENTS_LOADING
} from '../actions/actionTypes';

import { handleNetworkError, enqueueSnackbar } from './applicationMessageActions';
import { getNodeFaultScore, fetchFaultScores } from './homeActions';

import awsHeader from '../helpers/awsAuth';
import { k2aBaseCacheApi } from '../apis/key2act';
import { lambdaBaseCacheApi } from '../apis/lambda';
import { getSortOrder } from '../helpers/collections';
import { getErrorMessage } from '../helpers/errors';
import { findSiteId } from '../helpers/treeNode';
import { dataUrlToBlob } from '../utils/blob-util';
import { setBobLoading } from './bobActions';

const BuildingTypesUrl = template.parse('/building-types{?limit}');
const BusinessTypesUrl = template.parse('/business-types{?limit}');
const CampusUrl = template.parse('/campus/{campusId}');
const ClientCampusSiteUrl = template.parse('/companies/{companyId}/clients-campus-sites{?limit,sort,page}');
const CampusSiteUrl = template.parse('/campus/{campusId}/sites/{siteId}');
const CampusAndSitesUrl = template.parse('/clients/{clientId}/campus-sites{?searchFilter,limit}');
const CompaniesUrl = template.parse('/companies/{companyId}/clients{?searchFilter,limit,page}{&sort}');
const ContactsUrl = template.parse('/clients/{clientId}/contacts{?searchFilter,limit}');
const ClientCampusUrl = template.parse('/clients/{clientId}/campus{?searchFilter,limit}');
const ClientsUrl = template.parse('/clients/{clientId}');
const ConversionsUrl = template.parse('/companies/{companyId}/conversions{?searchFilter,limit}');
const EquipmentManufacturersUrl = template.parse('/equipment-manufacturers{?limit}');
const FuelTypesUrl = template.parse('/fuel-types{?limit}');
const SitesUrl = template.parse('/clients/{clientId}/sites{?searchFilter,limit,campusId}');
const WeatherStationUrl = template.parse('/weatherstations{?zipcode}');
const WeatherDataUrl = template.parse('/weatherdata{?siteId,startTimestamp,endTimestamp}');
const TechniciansDeleteUrl = template.parse('/servicetechnicians/{technicianId}');
const TechniciansGetUrl = template.parse('/servicetechnicians{?pagesize,page,companyId}');
const TechniciansUrl = template.parse('/servicetechnicians');
const TechnicianSpecialityUrl = template.parse('/technicianspecialties{?pagesize,page}');
const TechnicianSeniorityUrl = template.parse('/technicianseniorities{?pagesize,page}');

const EquipmentUrl = template.parse('/sites/{siteId}/equipment{?searchFilter,limit,page,AreaId,SystemId,extendedData}');
const SensorsUrl = template.parse('sites/{siteId}/sensors{?searchFilter,page,limit,parentId}');
const SystemsUrl = template.parse('/sites/{siteId}/systems{?searchFilter,limit,equipmentId}');
const AreasUrl = template.parse('/sites/{siteId}/areas{?searchFilter,limit,equipmentId}');

const ServiceCallStatusesUrl = template.parse('/servicecalls/statuses');
const ServiceAppointmentStatusesUrl = template.parse('/serviceappointments/statuses');

const SpecificContactUrl = template.parse('/clients/contacts/{contactId}');
const SpecificConversionUrl = template.parse('/conversions/{conversionId}');
const SpecificSiteUrl = template.parse('/sites/{siteId}');

const SpecificEquipmentUrl = template.parse('/equipment/{equipmentId}{?extendedData}');
const SpecificSystemUrl = template.parse('/systems/{systemId}');
const SpecificAreaUrl = template.parse('/areas/{areaId}');
const SpecificSensorUrl = template.parse('/sensors/{sensorId}{?resetcalculatedsensor}');

const AreaEquipmentUrl = template.parse('/areas/{areaId}/equipment/{equipmentId}');
const SystemEquipmentUrl = template.parse('/systems/{systemId}/equipment/{equipmentId}');

const ReassignSiteUrl = template.parse('/sites/{siteId}/reassign-client{?clientId}');
const UnitsUrl = template.parse('/units{?unitType,pageSize,page}');

const ErpAgentsUrl = template.parse('/agentconfigurations{?page,pageSize,orderby,companyId}');
const ErpClientsUrl = template.parse('/externalclients{?agentConfigurationId,page,pageSize}');
const ErpContactsUrl = template.parse('/externalcontacts{?externalSiteId,page,pageSize}');
const ErpSitesUrl = template.parse('/externalsites{?externalClientId,page,pageSize}');
const ErpEquipmentUrl = template.parse('/externalequipments{?externalSiteId,page,pageSize}');
const ErpStatusesUrl = template.parse('/externalstatuses{?agentConfigurationId,type,page,pageSize}');
const ErpTechniciansUrl = template.parse('/externaltechnicians{?agentConfigurationId,page,pageSize}');

const SpecificErpClientUrl = template.parse('/externalclients{?id}');
const SpecificErpSiteUrl = template.parse('/externalsites{?id}');
const SpecificErpEquipmentUrl = template.parse('/externalequipments{?id}');
const SpecificErpTechnicianUrl = template.parse('/externaltechnicians{?id}');
const SpecificErpContactUrl = template.parse('/externalcontacts{?id}');

const EntityMappingsGetByAgentIdUrl = template.parse('/entitymappings{?agentConfigurationId,erpEntityKind,pageSize,page}');
const EntityMappingsGetUrl = template.parse('/entitymappings{?erpEntityKind,name,entityId,externalId}');
const EntityMappingsPostUrl = template.parse('/entitymappings');
const EntityMappingsDeleteUrl = template.parse('/entitymappings/{linkId}');

const EquipmentByEquipmentId = template.parse('/equipment/{equipmentId}');
const SystemBySystemId = template.parse('/systems/{systemId}');

const ImportUrl = template.parse('/storage/technicianPhoto');

const DEFAULT_PAGE_LIMIT = 100;
const DEFAULT_SORT = 'name^asc';
const DEFAULT_PAGE_SIZE = 1000;
const DEFAULT_PAGE = 1;
const defaultManufacturer = '00000000-0000-0000-0000-000000000000';

export const architectActionRouter = (entity, entityId, action, parentId) => async (dispatch, getState) => {
    switch (action) {
        case 'Create':
            switch (entity) {
                case 'Areas':
                    await dispatch(fetchAreas(parentId));
                    dispatch(updateBucketNode(parentId, entityId, 'area', action));
                    break;
                case 'Campuses':
                    dispatch(updateClientChildrenWS(parentId));
                    break;
                case 'Clients':
                    dispatch(updateCompanyChildrenWS(entityId, parentId, action));
                    dispatch(fetchClientNodes(parentId));
                    break;
                case 'Contacts':
                    dispatch(fetchContacts(parentId));
                    break;
                case 'Equipment':
                    await dispatch(fetchEquipment(parentId));
                    dispatch(updateBucketNode(parentId, entityId, 'equipment', action));
                    break;
                case 'Sites':
                    dispatch(updateClientChildrenWS(parentId));
                    break;
                case 'Systems':
                    await dispatch(fetchSystems(parentId));
                    dispatch(updateBucketNode(parentId, entityId, 'system', action));
                    break;
                default:
                    break;
            }
            break;
        case 'Delete':
            switch (entity) {
                case 'Areas':
                    await dispatch(fetchAreas(parentId));
                    dispatch(updateBucketNode(parentId, entityId, 'area', action));
                    break;
                case 'Campuses':
                    dispatch(updateClientChildrenWS(parentId));
                    break;
                case 'Clients':
                    dispatch(updateCompanyChildrenWS(entityId, parentId, action));
                    dispatch(fetchClientNodes(parentId));
                    break;
                case 'Contacts':
                    dispatch(fetchContacts(parentId));
                    break;
                case 'Equipment':
                    dispatch(updateBucketNode(parentId, entityId, 'equipment', action));
                    break;
                case 'Sites':
                    dispatch(updateClientChildrenWS(parentId));
                    break;
                case 'Systems':
                    await dispatch(fetchSystems(parentId));
                    dispatch(updateBucketNode(parentId, entityId, 'system', action));
                    break;
                default:
                    break;
            }
            break;
        case 'Update':
            switch (entity) {
                case 'Areas':
                    await dispatch(fetchAreas(parentId));
                    dispatch(updateBucketNode(parentId, entityId, 'area', action));
                    break;
                case 'Campuses':
                    dispatch(fetchCampus(entityId));
                    break;
                case 'Clients':
                    dispatch(fetchClient(entityId));
                    dispatch(fetchClientNodes(parentId));
                    break;
                case 'Contacts':
                    dispatch(fetchContacts(parentId));
                    break;
                case 'Equipment':
                    await dispatch(fetchEquipment(parentId));
                    dispatch(updateBucketNode(parentId, entityId, 'equipment', action));
                    break;
                case 'Sites':
                    dispatch(fetchSite(entityId));
                    break;
                case 'Systems':
                    await dispatch(fetchSystems(parentId));
                    dispatch(updateBucketNode(parentId, entityId, 'system', action));
                    break;
                default:
                    break;
            }
            break;
        default:
            break;
    }
};

const doFileImport = async (file, technicianId, header) => {
    try {
        const fileImportResponse = await lambdaBaseCacheApi.put(
            ImportUrl.expand(),
            {
                technicianId
            },
            header
        );

        const blob = await dataUrlToBlob(file);

        await axios({
            method:  'put',
            url:     fileImportResponse.data.url,
            data:    blob,
            headers: {
                'Content-Type': 'image/png'
            }
        });
    } catch (error) {
        throw new Error(error);
    }
};

export const initializeTree = companyInfo => async (dispatch, getState) => {
    const {
        billingStatus,
        companyName,
        companyId,
        contractorType,
        companyAddress,
        enabled
    } = companyInfo;

    const treeType = getState().architect.treeType;

    try {
        if (treeType === 'home') {
            await dispatch(getNodeFaultScore(companyId));
        }

        const companyScore = getState().home.nodeScore;

        const rootNode = {
            billingStatus,
            enabled,
            contractorType,
            faultScore: companyScore,
            label:      companyName,
            name:       companyName,
            address:    companyAddress,
            key:        companyId,
            id:         companyId,
            type:       'company',
            nodes:      []
        };

        dispatch(setTree([rootNode]));
        dispatch(setNode(rootNode));

        Promise.all([
            dispatch(fetchClientsCampusAndSiteNodes(companyId)),
            dispatch(fetchClientNodes(companyId))
        ].map(p => p.catch(() => {}))).then(() => {});

    } catch (error) {
        dispatch(handleNetworkError(error));

        return false;
    }
};

export const checkNoExistingAgent = agentId => async (dispatch, getState) => {
    const links = getState().architect.links;

    const agent = links.find(link => link.agentConfigurationId === agentId);

    if (agent) return false; // return false to feed validator in CreateLinkModal.js

    return true;
};

export const checkExistingLinks = externalId => async (dispatch, getState) => {
    const header = await awsHeader();

    try {
        const externalResponse = await lambdaBaseCacheApi.get(
            EntityMappingsGetUrl.expand({
                erpEntityKind: 'site',
                externalId
            }),
            header
        );

        const externalPayload = _.get(externalResponse, 'data.data', []);

        if (!externalPayload.length) return '';

        const entityId = _.get(externalPayload[0], 'entityId', '');

        if (!entityId) return '';

        const entityResponse = await k2aBaseCacheApi.get(
            SpecificSiteUrl.expand({
                siteId: entityId
            }),
            header
        );

        const entityPayload = _.get(entityResponse, 'data', []);

        return _.get(entityPayload, 'name', '');
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const fetchSiteData = siteId => async (dispatch) => {
    const header = await awsHeader();
    
    dispatch(setBobLoading('fetchSite'));

    try {
        const response = await k2aBaseCacheApi.get(
            SpecificSiteUrl.expand({
                siteId
            }),
            header
        );

        return _.get(response, 'data', []);
    } catch (error) {
        dispatch(handleNetworkError(error));
    } finally {
        dispatch(setBobLoading('fetchSite'));
    }
};

export const fetchSystemsBySiteId = (siteId, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();
    
    try {
        const response = await k2aBaseCacheApi.get(
            SystemsUrl.expand({
                siteId,
                ...options
            }),
            header
        );

        const payload = _.get(response, 'data.data', []);

        return payload;
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const fetchEquipmentsBySiteId = (siteId, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    try {
        const response = await k2aBaseCacheApi.get(
            EquipmentUrl.expand({
                siteId,
                ...options
            }),
            header
        );

        const payload = _.get(response, 'data.data', []);

        return payload;
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const fetchEquipmentByEquipmentId = (equipmentId, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    dispatch(setBobLoading('fetchEquipment'));

    try {
        const response = await k2aBaseCacheApi.get(
            EquipmentByEquipmentId.expand({
                equipmentId,
                ...options
            }),
            header
        );
            
        const payload = _.get(response, 'data', []);
            
        return payload;
    } catch (error) {
        dispatch(handleNetworkError(error));
    } finally {
        dispatch(setBobLoading('fetchEquipment'));
    }
};

export const fetchSystemBySystemId = (systemId, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    dispatch(setBobLoading('fetchSystem'));

    try {
        const response = await k2aBaseCacheApi.get(
            SystemBySystemId.expand({
                systemId,
                ...options
            }),
            header
        );

        let payload = _.get(response, 'data', []);

        if (payload !== [] && payload.length === 1) {
            payload = payload[0];
        }
        return payload;
    } catch (error) {
        dispatch(handleNetworkError(error));
    } finally {
        dispatch(setBobLoading('fetchSystem'));
    }
};

export const createArchitectSite = (clientId, companyInfo, body, campusId = '', options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    try {
        const site = await k2aBaseCacheApi.post(
            SitesUrl.expand({
                clientId,
                ...options
            }),
            body,
            header
        );

        if (campusId !== '') {
            const siteId = _.get(site, 'data.id', '');

            await k2aBaseCacheApi.post(
                CampusSiteUrl.expand({
                    campusId,
                    siteId,
                    ...options
                }),
                body,
                header
            );
            await dispatch(fetchCampusSites(clientId, campusId));
        }

        dispatch({
            type:    TOGGLE_LOADING,
            payload: false
        });

        dispatch(enqueueSnackbar('Site created successfully.', 'success'));

        return _.get(site, 'data.id', true);
    } catch (error) {
        dispatch(handleNetworkError(error));

        return false;
    }
};

export const createArea = (siteId, body, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    try {
        await k2aBaseCacheApi.post(
            AreasUrl.expand({
                siteId,
                ...options
            }),
            body,
            header
        );

        dispatch({
            type:    TOGGLE_LOADING,
            payload: false
        });
        dispatch(enqueueSnackbar('Area created successfully.', 'success'));

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));

        return false;
    }
};

export const createAreaEquipment = (areaId, siteId, equipment, body = {}, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    try {
        await Promise.all(equipment.map(equipmentId => {
            return k2aBaseCacheApi.post(
                AreaEquipmentUrl.expand({
                    areaId,
                    equipmentId,
                    ...options
                }),
                body,
                header
            );
        }));

        options.AreaId = areaId;
        options.extendedData = true;

        const response = await k2aBaseCacheApi.get(
            EquipmentUrl.expand({
                siteId,
                ...options
            }),
            header
        );

        const payload = _.get(response, 'data.data', []);

        dispatch({
            type: FETCH_FILTERED_EQUIPMENT,
            payload
        });

        dispatch({
            type:    TOGGLE_LOADING,
            payload: false
        });
        dispatch(enqueueSnackbar('Equipment linked to Area successfully.', 'success'));

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));

        return false;
    }
};

export const createCampus = (companyInfo, clientId, body, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    try {
        await k2aBaseCacheApi.post(
            ClientCampusUrl.expand({
                clientId,
                ...options
            }),
            body,
            header
        );

        dispatch({
            type:    TOGGLE_LOADING,
            payload: false
        });

        dispatch(enqueueSnackbar('Campus created successfully.', 'success'));

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));

        return false;
    }
};

export const createCampusSite = (campusId, sites, clientId, body = {}, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    try {
        await Promise.all(sites.map(siteId => {
            return k2aBaseCacheApi.post(
                CampusSiteUrl.expand({
                    campusId,
                    siteId,
                    ...options
                }),
                body,
                header
            );
        }));

        dispatch({
            type:    TOGGLE_LOADING,
            payload: false
        });

        dispatch(enqueueSnackbar('Campus site(s) linked successfully.', 'success'));

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));

        return false;
    }
};

export const createConversion = (companyInfo, body, options = {}) => async (dispatch, getState) => {
    const { companyId } = companyInfo;
    const header = await awsHeader();

    try {
        await k2aBaseCacheApi.post(
            ConversionsUrl.expand({
                companyId,
                ...options
            }),
            body,
            header
        );

        await dispatch(fetchConversions(companyId));
        dispatch(enqueueSnackbar('Conversion created successfully.', 'success'));

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));

        return false;
    }
};

export const createClient = (companyInfo, body, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();
    const { companyId } = companyInfo;

    try {
        const client = await k2aBaseCacheApi.post(
            CompaniesUrl.expand({
                companyId,
                ...options
            }),
            body,
            header
        );

        dispatch({
            type:    TOGGLE_LOADING,
            payload: false
        });

        dispatch(enqueueSnackbar('Client created successfully.', 'success'));

        return _.get(client, 'data.id', '');
    } catch (error) {
        dispatch(handleNetworkError(error));

        return false;
    }
};

export const createContact = (clientId, body, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    try {
        await k2aBaseCacheApi.post(
            ContactsUrl.expand({
                clientId,
                ...options
            }),
            body,
            header
        );

        dispatch({
            type:    TOGGLE_LOADING,
            payload: false
        });
        dispatch(enqueueSnackbar('Contact created successfully.', 'success'));

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));

        return false;
    }
};

export const createEquipment = (siteId, body, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    try {
        const response = await k2aBaseCacheApi.post(
            EquipmentUrl.expand({
                siteId,
                ...options
            }),
            body,
            header
        );

        dispatch({
            type:    TOGGLE_LOADING,
            payload: false
        });
        dispatch(enqueueSnackbar('Equipment created successfully.', 'success'));

        return _.get(response, 'data', false);
    } catch (error) {
        dispatch(handleNetworkError(error));

        return false;
    }
};

export const createErpServiceCallMapping = (entityId, externalId, agentConfigurationId, erpEntityKind = 'status') => async(dispatch) => {
    const header = await awsHeader();
    const data = {
        erpEntityKind:        erpEntityKind,
        entityId:             entityId,
        externalId:           externalId,
        agentConfigurationId: agentConfigurationId
    };

    try {
        await lambdaBaseCacheApi.post(
            EntityMappingsPostUrl.expand({}),
            data,
            header
        );

        await dispatch(fetchErpServiceCallMapping(agentConfigurationId));

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));
        return false;
    }
};

export const createSensor = (companyInfo, siteId, parentId, body, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();
    const rawUnits = getState().architect.rawUnits;

    if (_.isNil(body.calculatedSensor)) {
        body.calculatedSensor = false;
        body.sensorCalculationId = null;
        body.calculatedSensorIds = null;
    }

    try {
        await k2aBaseCacheApi.post(
            SensorsUrl.expand({
                siteId,
                ...options
            }),
            body,
            header
        );

        options.page = 1;
        options.limit = DEFAULT_PAGE_LIMIT;
        const response = await k2aBaseCacheApi.get(
            SensorsUrl.expand({
                siteId,
                parentId,
                ...options
            }),
            header
        );

        const payload = _getSensors(response, rawUnits);

        dispatch({
            type: FETCH_SENSORS,
            payload
        });

        dispatch({
            type:    TOGGLE_LOADING,
            payload: false
        });

        dispatch(enqueueSnackbar('Sensor created successfully.', 'success'));

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));

        return false;
    }
};

export const createEquipmentArea = (area, equipmentId, siteId, body, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    try {
        await Promise.all(area.map(areaId => {
            return k2aBaseCacheApi.post(
                AreaEquipmentUrl.expand({
                    areaId,
                    equipmentId,
                    ...options
                }),
                body,
                header
            );
        }));

        const response = await k2aBaseCacheApi.get(
            AreasUrl.expand({
                siteId,
                equipmentId,
                ...options
            }),
            header
        );

        const payload = _.chain(response).get('data.data', []).uniqBy('id').value();

        dispatch({
            type: FETCH_FILTERED_AREAS,
            payload
        });

        dispatch({
            type:    TOGGLE_LOADING,
            payload: false
        });
        dispatch(enqueueSnackbar('Equipment linked to Area(s) successfully.', 'success'));

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));

        return false;
    }
};

export const createEquipmentSystem = (equipmentId, siteId, systems, body = {}, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    try {
        await Promise.all(systems.map(systemId => {
            return k2aBaseCacheApi.post(
                SystemEquipmentUrl.expand({
                    systemId,
                    equipmentId,
                    ...options
                }),
                body,
                header
            );
        }));

        const response = await k2aBaseCacheApi.get(
            SystemsUrl.expand({
                siteId,
                equipmentId,
                ...options
            }),
            header
        );

        const payload = _.get(response, 'data.data', []);

        dispatch({
            type: FETCH_FILTERED_SYSTEMS,
            payload
        });

        dispatch({
            type:    TOGGLE_LOADING,
            payload: false
        });
        dispatch(enqueueSnackbar('Equipment linked to System(s) successfully.', 'success'));

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));

        return false;
    }
};

export const createLink = (body) => async (dispatch) => {
    const header = await awsHeader();

    try {
        await lambdaBaseCacheApi.post(
            EntityMappingsPostUrl.expand(),
            body,
            header
        );

        await dispatch(fetchErpLinks(body.erpEntityKind, {entityId: body.entityId}));

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));

        return false;
    }
};

export const fetchContactLinks = (contactIds, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    let payload = [];
    for (const id of contactIds) {
        try {
            const response = await lambdaBaseCacheApi.get(
                EntityMappingsGetUrl.expand({
                    name:          options.searchFilter,
                    erpEntityKind: 'contact',
                    entityId:      id
                }),
                header
            );
            const result = _.get(response, 'data.data', {});
            if (result.length) payload.push(result[0]);
        } catch (error) {
            dispatch(handleNetworkError(error));
        }
    }

    const getNames = async (link) => {
        if (link) {
            const erpEntity = await dispatch(fetchErpEntity(link));
            link.name = erpEntity.name;
            return erpEntity;
        }
    };

    await Promise.all(payload.map(getNames));

    dispatch({
        type: FETCH_CONTACT_LINKS,
        payload
    });
};

export const fetchErpLinks = (linkType, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();
    switch (linkType) {
        case 'contact':
            dispatch({
                type: BEGIN_FETCH_CONTACT_LINKS
            });
            break;
        case 'equipment':
            options.entityId || (options.entityId = getState().architect.selectedNode.id);

            dispatch({
                type: BEGIN_FETCH_LINKS
            });
            break;
        case 'client':
            options.entityId || (options.entityId = getState().architect.selectedNode.id);

            dispatch({
                type: BEGIN_FETCH_LINKS
            });
            break;
        case 'site':
            options.entityId || (options.entityId = getState().architect.selectedNode.id);

            dispatch({
                type: BEGIN_FETCH_LINKS
            });
            break;
        default:
            dispatch({
                type: BEGIN_FETCH_LINKS
            });
            break;
    }

    try {
        options.searchFilter || (options.searchFilter = '');

        const response = await lambdaBaseCacheApi.get(
            EntityMappingsGetUrl.expand({
                erpEntityKind: linkType,
                ...options
            }),
            header
        );
        let payload = _.get(response, 'data.data', []);

        const getNames = async (link) => {
            const erpEntity = await dispatch(fetchErpEntity(link));
            if (linkType === 'technician') {
                erpEntity.name = `${erpEntity.firstName} ${erpEntity.lastName}`;
            }
            link.name = erpEntity.name;
            return erpEntity;
        };

        await Promise.all(payload.map(getNames));

        if (linkType && linkType.toLowerCase() === 'contact') {
            dispatch({
                type: FETCH_CONTACT_LINKS,
                payload
            });
        } else {
            dispatch({
                type: FETCH_LINKS,
                payload
            });
        }
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const createSystem = (siteId, body, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    try {
        const response = await k2aBaseCacheApi.post(
            SystemsUrl.expand({
                siteId,
                ...options
            }),
            body,
            header
        );

        dispatch({
            type:    TOGGLE_LOADING,
            payload: false
        });
        dispatch(enqueueSnackbar('System created successfully.', 'success'));

        return _.get(response, 'data', false);
    } catch (error) {
        dispatch(handleNetworkError(error));

        return false;
    }
};

export const createSystemEquipment = (systemId, siteId, equipment, body = {}, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    try {
        await Promise.all(equipment.map(equipmentId => {
            return k2aBaseCacheApi.post(
                SystemEquipmentUrl.expand({
                    systemId,
                    equipmentId,
                    ...options
                }),
                body,
                header
            );
        }));

        options.SystemId = systemId;
        options.extendedData = true;

        const response = await k2aBaseCacheApi.get(
            EquipmentUrl.expand({
                siteId,
                ...options
            }),
            header
        );

        const payload = _.get(response, 'data.data', []);

        dispatch({
            type: FETCH_FILTERED_EQUIPMENT,
            payload
        });

        dispatch({
            type:    TOGGLE_LOADING,
            payload: false
        });
        dispatch(enqueueSnackbar('Equipment linked to System successfully.', 'success'));

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));

        return false;
    }
};

export const deleteArea = (areaId, siteId, clientId) => async (dispatch, getState) => {
    const header = await awsHeader();

    try {
        await k2aBaseCacheApi.delete(
            SpecificAreaUrl.expand({ areaId }),
            {
                headers: header.headers
            }
        );

        dispatch(enqueueSnackbar('Area deleted successfully.', 'success'));

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));
        return false;
    }
};

export const deleteAreaEquipment = (areaId, equipmentId, siteId, options = {}) => async dispatch => {
    const header = await awsHeader();

    try {
        await k2aBaseCacheApi.delete(
            AreaEquipmentUrl.expand({ areaId, equipmentId }),
            {
                headers: header.headers
            }
        );

        options.AreaId = areaId;
        options.limit = DEFAULT_PAGE_LIMIT;
        options.extendedData = true;

        const response = await k2aBaseCacheApi.get(
            EquipmentUrl.expand({
                siteId,
                ...options
            }),
            header
        );

        const payload = _.get(response, 'data.data', []);

        dispatch({
            type: FETCH_FILTERED_EQUIPMENT,
            payload
        });

        dispatch(enqueueSnackbar('Equipment removed from Area.', 'success'));

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));
        return false;
    }
};

export const deleteCampus = (campusId, clientId) => async (dispatch, getState) => {
    const header = await awsHeader();

    const tree = getState().architect.tree;

    try {
        await k2aBaseCacheApi.delete(
            CampusUrl.expand({ campusId }),
            {
                headers: header.headers
            }
        );

        const updatedTree = _removeNode(tree, campusId);
        const parentClient = _searchTree(tree, clientId);

        dispatch(setTree(updatedTree));
        dispatch(setNode(parentClient));

        dispatch(enqueueSnackbar('Campus deleted successfully.', 'success'));

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));
        return false;
    }
};

export const deleteCampusSites = (campus, campusSites, client) => async dispatch => {
    const header = await awsHeader();
    const clientId = client.id;
    const campusId = campus.id;

    try {
        await Promise.all(campusSites.map(site => {
            const siteId = site.id;
            return k2aBaseCacheApi.delete(
                CampusSiteUrl.expand({ campusId, siteId }),
                {
                    headers: header.headers
                }
            );
        }));

        dispatch(enqueueSnackbar('Site(s) removed from Campus', 'success'));
        await dispatch(fetchCampusSites(clientId, campusId));

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));
        return false;
    }
};

export const deleteClient = (clientId, companyInfo) => async (dispatch, getState) => {
    const header = await awsHeader();

    const tree = getState().architect.tree;
    const clientNodes = getState().architect.clientNodes;

    try {
        await k2aBaseCacheApi.delete(
            ClientsUrl.expand({ clientId }),
            {
                headers: header.headers
            }
        );

        const updatedTree = _removeNode(tree, clientId);
        const updatedClientNodes = clientNodes.filter(node => node.id !== clientId);

        dispatch(setTree(updatedTree));

        dispatch({
            type:    FETCH_CLIENT_NODES,
            payload: updatedClientNodes
        });

        dispatch(setNode(tree[0]));

        dispatch(enqueueSnackbar('Client deleted successfully.', 'success'));

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));
        return false;
    }
};

export const deleteContact = (contactId, clientId) => async dispatch => {
    const header = await awsHeader();

    try {
        await k2aBaseCacheApi.delete(
            SpecificContactUrl.expand({ contactId }),
            {
                headers: header.headers
            }
        );

        dispatch(enqueueSnackbar('Contact deleted successfully.', 'success'));
        await dispatch(fetchContacts(clientId));
        await dispatch(setClientNode(clientId));

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));
        return false;
    }
};

export const deleteConversion = (conversionId, companyInfo) => async dispatch => {
    const { companyId } = companyInfo;
    const header = await awsHeader();

    try {
        await k2aBaseCacheApi.delete(
            SpecificConversionUrl.expand({ conversionId }),
            {
                headers: header.headers
            }
        );

        dispatch(enqueueSnackbar('Conversion deleted successfully.', 'success'));
        await dispatch(fetchConversions(companyId));

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));
        return false;
    }
};

export const deleteEquipment = (equipmentId, siteId, clientId) => async (dispatch, getState) => {
    const header = await awsHeader();

    try {
        await k2aBaseCacheApi.delete(
            SpecificEquipmentUrl.expand({ equipmentId }),
            {
                headers: header.headers
            }
        );

        dispatch(enqueueSnackbar('Equipment deleted successfully.', 'success'));

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));
        return false;
    }
};

export const deleteEquipmentBulk = (equipment, siteId) => async (dispatch, getState) => {
    const header = await awsHeader();

    try {
        const promises = [];

        equipment.forEach(item => {
            const { id: equipmentId } = item;

            promises.push(
                k2aBaseCacheApi.delete(
                    SpecificEquipmentUrl.expand({ equipmentId }),
                    {
                        headers: header.headers
                    }
                )
            );
        });

        await Promise.all(promises.map(p => p.catch(err => err)));

        dispatch(enqueueSnackbar('Equipment deleted successfully.', 'success'));
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const deleteLink = (linkId, linkType) => async dispatch => {
    const header = await awsHeader();
    try {
        await lambdaBaseCacheApi.delete(
            EntityMappingsDeleteUrl.expand({
                linkId
            }),
            header
        );

        await dispatch(fetchErpLinks(linkType, {entityId: linkId}));

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));

        return false;
    }
};

export const deleteStatusMapping = linkId => async dispatch => {
    const header = await awsHeader();

    try {
        await lambdaBaseCacheApi.delete(
            EntityMappingsDeleteUrl.expand({
                linkId
            }),
            header
        );

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));

        return false;
    }
};

export const deleteSensor = (sensorId, parentId, siteId, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();
    const rawUnits = getState().architect.rawUnits;

    try {
        await k2aBaseCacheApi.delete(
            SpecificSensorUrl.expand({ sensorId }),
            {
                headers: header.headers
            }
        );

        options.page = 1;
        options.limit = DEFAULT_PAGE_LIMIT;
        const response = await k2aBaseCacheApi.get(
            SensorsUrl.expand({
                siteId,
                parentId,
                ...options
            }),
            header
        );

        const payload = _getSensors(response, rawUnits);

        dispatch({
            type: FETCH_SENSORS,
            payload
        });

        dispatch(enqueueSnackbar('Sensor deleted successfully.', 'success'));

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));
        return false;
    }
};

export const deleteSite = (siteId, clientId) => async (dispatch, getState) => {
    const header = await awsHeader();

    const tree = getState().architect.tree;

    try {
        await k2aBaseCacheApi.delete(
            SpecificSiteUrl.expand({ siteId }),
            {
                headers: header.headers
            }
        );

        const updatedTree = _removeNode(tree, siteId);
        const parentClient = _searchTree(tree, clientId);

        dispatch(setTree(updatedTree));
        dispatch(setNode(parentClient));

        dispatch(enqueueSnackbar('Site deleted successfully.', 'success'));

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));
        return false;
    }
};

export const deleteSystem = (systemId, siteId, clientId) => async (dispatch, getState) => {
    const header = await awsHeader();

    try {
        await k2aBaseCacheApi.delete(
            SpecificSystemUrl.expand({ systemId }),
            {
                headers: header.headers
            }
        );

        dispatch(enqueueSnackbar('System deleted successfully.', 'success'));

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));
        return false;
    }
};

export const deleteSystemEquipment = (systemId, equipmentId, siteId, options = {}) => async dispatch => {
    const header = await awsHeader();

    try {
        await k2aBaseCacheApi.delete(
            SystemEquipmentUrl.expand({ systemId, equipmentId }),
            {
                headers: header.headers
            }
        );

        options.SystemId = systemId;
        options.limit = DEFAULT_PAGE_LIMIT;
        options.extendedData = true;

        const response = await k2aBaseCacheApi.get(
            EquipmentUrl.expand({
                siteId,
                ...options
            }),
            header
        );

        const payload = _.get(response, 'data.data', []);

        dispatch({
            type: FETCH_FILTERED_EQUIPMENT,
            payload
        });

        dispatch(enqueueSnackbar('Equipment disassociated from System.', 'success'));

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));
        return false;
    }
};

export const deleteEquipmentSystem = (equipmentId, systemId, siteId, options = {}) => async dispatch => {
    const header = await awsHeader();

    try {
        await k2aBaseCacheApi.delete(
            SystemEquipmentUrl.expand({ equipmentId, systemId }),
            {
                headers: header.headers
            }
        );

        options.limit = DEFAULT_PAGE_LIMIT;
        const response = await k2aBaseCacheApi.get(
            SystemsUrl.expand({
                siteId,
                equipmentId,
                ...options
            }),
            header
        );

        const payload = _.get(response, 'data.data', []);

        dispatch({
            type: FETCH_FILTERED_SYSTEMS,
            payload
        });

        dispatch(enqueueSnackbar('System disassociated from Equipment.', 'success'));

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));
        return false;
    }
};

export const unlinkEquipmentSystem = (systemId, equipmentId) => async (dispatch, getState) => {
    const header = await awsHeader();

    try {
        await k2aBaseCacheApi.delete(
            SystemEquipmentUrl.expand({ equipmentId, systemId }),
            {
                headers: header.headers
            }
        );

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));

        return false;
    }
};

export const fetchRawUnits = (options = {}) => async dispatch => {
    const header = await awsHeader();

    options.pageSize = 1000;
    options.page = 1;

    dispatch(setBobLoading('fetchRawUnits'));

    try {
        const response = await lambdaBaseCacheApi.get(
            UnitsUrl.expand({
                ...options
            }),
            header
        );
        const payload = _.get(response, 'data.data', []);

        const conversion = [];
        const unitTypes = [];

        payload.forEach(item => {
            let type = item.unitType;

            if (conversion[type]) {
                conversion[type].units.push(item);
            } else {
                conversion[type] = {
                    units: [item]
                };
            }
        });

        let unitNames = Object.keys(conversion);

        unitNames.forEach(name => {
            unitTypes.push({
                name,
                items: [...conversion[name].units].sort(_sorter)
            });
        });

        unitTypes.forEach(unitType => {
            unitType.items.forEach(item => {
                item.name = `${item.name} (${item.displayValue})`;
            });
        });

        dispatch({
            type:    FETCH_RAW_UNITS,
            payload: unitTypes.sort(_sorter)
        });
        dispatch(setBobLoading('fetchRawUnits'));
    } catch (error) {
        dispatch(handleNetworkError(error));
        dispatch(setBobLoading('fetchRawUnits'));
    }
};

export const fetchBuildingTypes = (options = {}) => async (dispatch) => {
    const header = await awsHeader();

    options.limit || (options.limit = DEFAULT_PAGE_LIMIT);

    try {
        const response = await k2aBaseCacheApi.get(
            BuildingTypesUrl.expand({
                ...options
            }),
            header
        );
        const payload = _.get(response, 'data.data', []);

        dispatch({
            type: FETCH_BUILDING_TYPES,
            payload
        });
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const fetchAreas = (node, site = {}, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();
    let siteId;

    options.limit || (options.limit = DEFAULT_PAGE_LIMIT);

    if (_.isObject(node)) {
        siteId = _extractSiteId(node);
    } else {
        siteId = node;
    }

    try {
        const response = await k2aBaseCacheApi.get(
            AreasUrl.expand({
                siteId,
                ...options
            }),
            header
        );

        const payload = _.get(response, 'data.data', []);

        dispatch({
            type: FETCH_CORE_AREAS,
            payload
        });

        let siteData;
        if (_.isEmpty(site)) {
            siteData = await _fetchSiteData(siteId);
        } else {
            siteData = { ...site };
        }

        let processedPayload;

        if (payload.length) {
            processedPayload = payload.map(item => _prepareChildlessNode(item, siteData, 'areaItem'));
        } else {
            processedPayload = [];
        }

        dispatch({
            type:    FETCH_AREAS,
            payload: processedPayload
        });
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const fetchAreasByEquipmentId = (node, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    options.limit || (options.limit = DEFAULT_PAGE_LIMIT);
    options.equipmentId = node.id;

    const siteId = findSiteId(node);

    try {
        const response = await k2aBaseCacheApi.get(
            AreasUrl.expand({
                siteId,
                ...options
            }),
            header
        );

        const payload = _.chain(response).get('data.data', []).uniqBy('id').value();

        dispatch({
            type: FETCH_FILTERED_AREAS,
            payload
        });

    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const fetchBusinessTypes = (options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    options.limit || (options.limit = DEFAULT_PAGE_LIMIT);
    options.sort || (options.sort = DEFAULT_SORT);

    try {
        const response = await k2aBaseCacheApi.get(
            BusinessTypesUrl.expand({
                ...options
            }),
            header
        );
        const payload = _.get(response, 'data.data', []);

        dispatch({
            type: FETCH_BUSINESS_TYPES,
            payload
        });
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const fetchCampus = (campusId, options = {}) => async (dispatch) => {
    const header = await awsHeader();

    try {
        const result = await k2aBaseCacheApi.get(
            CampusUrl.expand({
                campusId
            }),
            header
        );

        const payload = _.get(result, 'data', []);
        const preparedPayload = _updateNode(payload, 'campus');

        dispatch(setNodeUpdate(preparedPayload));
    } catch (error) {
        dispatch(handleNetworkError(error));

        return false;
    }
};

export const fetchClient = (clientId, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    dispatch(setBobLoading('fetchingClient'));

    try {
        const result = await k2aBaseCacheApi.get(
            ClientsUrl.expand({
                clientId
            }),
            header
        );

        const payload = _.get(result, 'data', []);
        const preparedPayload = _updateNode(payload, 'client');

        dispatch(setClientNode(clientId));
        dispatch(setNodeUpdate(preparedPayload));
    } catch (error) {
        dispatch(handleNetworkError(error));
    } finally {
        dispatch(setBobLoading('fetchingClient'));
    }
};

export const fetchErpAgentConfigurations = (options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    let { companyId } = getState().companyInfo;
    const queryValues = queryString.parse(window.location.search);

    if (queryValues.company && companyId !== queryValues.company) {
        companyId = queryValues.company;
    }
    
    options.companyId = companyId;

    let totalPayload = [];

    try {

        const getPayload = async (page = 1) => {
            options.limit = DEFAULT_PAGE_LIMIT;
            options.sort = DEFAULT_SORT;
            options.page = page;

            const response = await lambdaBaseCacheApi.get(
                ErpAgentsUrl.expand({
                    ...options
                }),
                header
            );

            let payload = _.get(response, 'data.data', []);
            const totalCount = _.get(response, 'data.meta.totalCount', 0);

            totalPayload = totalPayload.concat(payload);

            page++;

            if (payload.length > 0 && totalPayload.length < totalCount) {
                return await getPayload(page);
            }

            return;
        };

        await getPayload();

        const payload = [
            {
                items:     [...totalPayload].sort(_sorter),
                name:      'Agents',
                itemProps: {
                    type: 'agents'
                }
            }
        ];

        dispatch({
            type: FETCH_ERP_AGENT_CONFIGURATIONS,
            payload
        });
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const fetchConversions = (companyId, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    options.limit || (options.limit = DEFAULT_PAGE_LIMIT);

    try {
        const response = await k2aBaseCacheApi.get(
            ConversionsUrl.expand({
                companyId,
                ...options
            }),
            header
        );
        const payload = _.get(response, 'data.data', []).sort((a, b) => _sorter(a, b));

        dispatch({
            type: FETCH_CONVERSIONS,
            payload
        });
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const fetchErpStatuses = (agentConfigurationId, options = {}, types) => async dispatch => {
    const header = await awsHeader();

    options.pageSize || (options.pageSize = DEFAULT_PAGE_SIZE);
    options.page || (options.page = DEFAULT_PAGE);

    try {
        let serviceCallStatuses = [];

        if (types && types.includes('servicecallstatus')) {
            options.type = 'servicecallstatus';
            const response = await lambdaBaseCacheApi.get(
                ErpStatusesUrl.expand({
                    agentConfigurationId,
                    ...options
                }),
                header
            );

            serviceCallStatuses = _.get(response, 'data.data', []);
        }
        let serviceAppointmentStatuses = [];
        if (types && types.includes('serviceappointmentstatus')) {
            options.type = 'serviceappointmentstatus';
            const responseTwo = await lambdaBaseCacheApi.get(
                ErpStatusesUrl.expand({
                    agentConfigurationId,
                    ...options
                }),
                header
            );
            serviceAppointmentStatuses = _.get(responseTwo, 'data.data', []);
        }

        else {
            const response = await lambdaBaseCacheApi.get(
                ErpStatusesUrl.expand({
                    agentConfigurationId,
                    ...options
                }),
                header
            );

            serviceCallStatuses = _.get(response, 'data.data', []);
        }

        const payload = serviceCallStatuses.concat(serviceAppointmentStatuses);

        dispatch({
            type: FETCH_ERP_STATUSES,
            payload
        });
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const fetchErpTechnicians = (agentConfigurationId, options = {}) => async dispatch => {
    const header = await awsHeader();

    options.pageSize || (options.pageSize = DEFAULT_PAGE_SIZE);
    options.page || (options.page = DEFAULT_PAGE);

    try {
        const response = await lambdaBaseCacheApi.get(
            ErpTechniciansUrl.expand({
                agentConfigurationId,
                ...options
            }),
            header
        );
        const techniciansPayload = _.get(response, 'data.data', []);
        techniciansPayload.forEach(technician => technician.name = `${technician.firstName || ''} ${technician.lastName || ''}`);

        const payload = [
            {
                items:     [...techniciansPayload].sort(_sorter),
                name:      'Technicians',
                itemProps: {
                    type: 'technicians'
                }
            }
        ];

        dispatch({
            type: FETCH_ERP_TECHNICIANS,
            payload
        });
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const fetchErpClients = (agentId, options = {}) => async dispatch => {
    const header = await awsHeader();
    dispatch(toggleErpClientsLoading(true));
    options.pageSize || (options.pageSize = DEFAULT_PAGE_SIZE);
    options.page || (options.page = DEFAULT_PAGE);

    try {
        const response = await lambdaBaseCacheApi.get(
            ErpClientsUrl.expand({
                agentConfigurationId: agentId,
                ...options
            }),
            header
        );

        const clientsPayload = _.get(response, 'data.data', []);

        const payload = [
            {
                items:     [...clientsPayload].sort(_sorter),
                name:      'Clients',
                itemProps: {
                    type: 'clients'
                }
            }
        ];
        dispatch(toggleErpClientsLoading(false));
        dispatch({
            type: FETCH_ERP_CLIENTS,
            payload
        });
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const fetchErpContacts = (siteId, options = {}) => async dispatch => {
    const header = await awsHeader();
    options.pageSize || (options.pageSize = DEFAULT_PAGE_SIZE);
    options.page || (options.page = DEFAULT_PAGE);

    try {
        const response = await lambdaBaseCacheApi.get(
            ErpContactsUrl.expand({
                externalSiteId: siteId,
                ...options
            }),
            header
        );

        const contactsPayload = _.get(response, 'data.data', []);

        const payload = [
            {
                items:     [...contactsPayload].sort(_sorter),
                name:      'Contacts',
                itemProps: {
                    type: 'contacts'
                }
            }
        ];

        dispatch({
            type: FETCH_ERP_CONTACTS,
            payload
        });
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const fetchErpDrilldown = (level, id) => dispatch => {
    if (level === 'agents') {
        dispatch({
            type:    FETCH_ERP_EQUIPMENT,
            payload: []
        });
        dispatch({
            type:    FETCH_ERP_SITES,
            payload: []
        });
        dispatch({
            type:    FETCH_ERP_CONTACTS,
            payload: []
        });
        dispatch(fetchErpClients(id));
        dispatch(fetchErpTechnicians(id));
    } else if (level === 'clients') {
        dispatch({
            type:    FETCH_ERP_EQUIPMENT,
            payload: []
        });
        dispatch({
            type:    FETCH_ERP_CONTACTS,
            payload: []
        });
        dispatch(fetchErpSites(id));
    } else if (level === 'sites') {
        dispatch(fetchErpEquipment(id));
        dispatch(fetchErpContacts(id));
    }
};

export const fetchErpSites = (clientId, options = {}) => async dispatch => {
    const header = await awsHeader();
    options.pageSize || (options.pageSize = DEFAULT_PAGE_SIZE);
    options.page || (options.page = DEFAULT_PAGE);

    try {
        const response = await lambdaBaseCacheApi.get(
            ErpSitesUrl.expand({
                externalClientId: clientId,
                ...options
            }),
            header
        );

        const sitesPayload = _.get(response, 'data.data', []);

        const payload = [
            {
                items:     [...sitesPayload].sort(_sorter),
                name:      'Sites',
                itemProps: {
                    type: 'sites'
                }
            }
        ];

        dispatch({
            type: FETCH_ERP_SITES,
            payload
        });
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};


export const fetchErpServiceApptMapping = (agentConfigurationId, options = {}) => async dispatch => {
    const header = await awsHeader();
    options.erpEntityKind = 'serviceappointmentstatus';
    options.pageSize = DEFAULT_PAGE_SIZE;

    try {
        const response = await lambdaBaseCacheApi.get(
            EntityMappingsGetByAgentIdUrl.expand({
                agentConfigurationId,
                ...options
            }),
            header
        );

        const payload = _.get(response, 'data.data', []);

        dispatch({
            type: FETCH_ERP_SERVICE_APPOINTMENT_MAPPING,
            payload
        });
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const fetchErpServiceCallMapping = (agentConfigurationId, options = {}) => async dispatch => {
    const header = await awsHeader();
    options.erpEntityKind = 'servicecallstatus';
    options.pageSize = DEFAULT_PAGE_SIZE;
    options.page = 1;

    try {
        const response = await lambdaBaseCacheApi.get(
            EntityMappingsGetByAgentIdUrl.expand({
                agentConfigurationId,
                ...options
            }),
            header
        );

        const payload = _.get(response, 'data.data', []);

        dispatch({
            type: FETCH_ERP_SERVICE_CALL_MAPPING,
            payload
        });
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const fetchErpEquipment = (siteId, options = {}) => async dispatch => {
    const header = await awsHeader();
    options.pageSize || (options.pageSize = DEFAULT_PAGE_SIZE);
    options.page || (options.page = DEFAULT_PAGE);

    try {
        const response = await lambdaBaseCacheApi.get(
            ErpEquipmentUrl.expand({
                externalSiteId: siteId,
                ...options
            }),
            header
        );

        const equipmentPayload = _.get(response, 'data.data', []);

        const payload = [
            {
                items:     [...equipmentPayload].sort(_sorter),
                name:      'Equipment',
                itemProps: {
                    type: 'equipment'
                }
            }
        ];

        dispatch({
            type: FETCH_ERP_EQUIPMENT,
            payload
        });
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

const fetchErpEntity = link => async (dispatch) => {
    const header = await awsHeader();
    let path;
    if (link.erpEntityKind === 'client') {
        path = SpecificErpClientUrl;
    } else if (link.erpEntityKind === 'site') {
        path = SpecificErpSiteUrl;
    } else if (link.erpEntityKind === 'equipment') {
        path = SpecificErpEquipmentUrl;
    } else if (link.erpEntityKind === 'technician') {
        path = SpecificErpTechnicianUrl;
    } else if (link.erpEntityKind === 'contact') {
        path = SpecificErpContactUrl;
    }

    try {
        const response = await lambdaBaseCacheApi.get(
            path.expand({
                id: link.externalId
            }),
            header
        );
        return _.get(response, 'data.data[0]', []);
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const fetchEquipmentManufacturers = (options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    options.limit || (options.limit = DEFAULT_PAGE_LIMIT);

    try {
        const response = await k2aBaseCacheApi.get(
            EquipmentManufacturersUrl.expand({
                ...options
            }),
            header
        );
        const payload = _.get(response, 'data.data', []);

        dispatch({
            type: FETCH_EQUIPMENT_MANUFACTURERS,
            payload
        });
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const fetchCampusSites = (clientId, campusId, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    options.limit || (options.limit = DEFAULT_PAGE_LIMIT);
    options.campusId = campusId;

    try {
        const response = await k2aBaseCacheApi.get(
            SitesUrl.expand({
                clientId,
                ...options
            }),
            header
        );

        const payload = _.get(response, 'data.data', []);

        dispatch({
            type:    FETCH_CAMPUS_SITES,
            payload: payload
        });
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const fetchClientsCampusAndSiteNodes = (companyId, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    const treeType = getState().architect.treeType;
    const tree = getState().architect.tree;

    let totalPayload = [];

    try {
        const prepareHomeTree = async currentPayload => {
            const clientIds = currentPayload.map(node => node.client.id);
            const clientFaultScores = await dispatch(fetchFaultScores(clientIds)) || [];

            const siteIds = [];
            const campusIds = [];
            currentPayload.forEach(node => {
                if (node.campusSites.siteList.length) {
                    node.campusSites.siteList.forEach(site => {
                        siteIds.push(site.id);
                    });
                }

                if (node.campusSites.campusList.length) {
                    node.campusSites.campusList.forEach(campus => {
                        campusIds.push(campus.id);

                        if (campus.siteList.length) {
                            campus.siteList.forEach(campusSite => {
                                siteIds.push(campusSite.id);
                            });
                        }
                    });
                }
            });

            const siteFaultScores = await dispatch(fetchFaultScores(siteIds)) || [];
            const campusFaultScores = await dispatch(fetchFaultScores(campusIds)) || [];

            currentPayload.forEach(node => {
                clientFaultScores.forEach(client => {
                    if (node.client.id === client.id) {
                        node.client.faultScore = _setScore(client);
                    }
                });
            });

            currentPayload.forEach(node => {
                if (node.campusSites.siteList.length) {
                    node.campusSites.siteList.forEach(site => {
                        siteFaultScores.forEach(siteScore => {
                            if (site.id === siteScore.id) {
                                site.faultScore = _setScore(siteScore);
                            }
                        });
                    });
                }

                if (node.campusSites.campusList.length) {
                    node.campusSites.campusList.forEach(campus => {
                        campusFaultScores.forEach(campusScore => {
                            if (campus.id === campusScore.id) {
                                campus.faultScore = _setScore(campusScore);
                            }
                        });

                        if (campus.siteList.length) {
                            campus.siteList.forEach(campusSite => {
                                siteFaultScores.forEach(siteScore => {
                                    if (campusSite.id === siteScore.id) {
                                        campusSite.faultScore = _setScore(siteScore);
                                    }
                                });
                            });
                        }
                    });
                }
            });

            return currentPayload;
        };

        const getPayload = async (page = 1) => {
            options.limit = DEFAULT_PAGE_LIMIT;
            options.sort = DEFAULT_SORT;
            options.page = page;

            const response = await k2aBaseCacheApi.get(
                ClientCampusSiteUrl.expand({
                    companyId,
                    ...options
                }),
                header
            );

            let payload = _.get(response, 'data.data', []);
            const totalCount = _.get(response, 'data.meta.totalCount', 0);

            if (treeType === 'home') {
                payload = await prepareHomeTree(payload);
            }

            const preparedPayload = _prepareNodes(payload);

            totalPayload = [...totalPayload, ...preparedPayload];
            tree[0].nodes = [...totalPayload];

            dispatch(setTree(tree));

            page++;

            if (payload.length > 0 && totalPayload.length < totalCount) {
                return await getPayload(page);
            }

            return;
        };

        await getPayload();

    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const fetchCampusAndSiteNodes = (clientId, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();
    header.headers.extendedData = 'true';

    options.limit || (options.limit = DEFAULT_PAGE_LIMIT);
    
    const treeType = getState().architect.treeType;

    try {
        const response = await k2aBaseCacheApi.get(
            CampusAndSitesUrl.expand({
                clientId,
                ...options
            }),
            header
        );

        const campusPayload = _.get(response, 'data.campusList', []);
        const sitePayload = _.get(response, 'data.siteList', []);

        let campusList = campusPayload.map(campus => _prepareNode(campus, 'campus'));

        const siteList = sitePayload.map(site => _prepareNode(site, 'site'));

        campusList = _.forEach(campusList, campus => {
            _.set(campus, 'nodes', campus.siteList);
            _.unset(campus, 'siteList');

            campus.nodes = campus.nodes.map(node => _prepareNode(node, 'site'));
        });

        const campusIds = campusList.map(campus => {
            return campus.id;
        });

        if (treeType === 'home') {
            const campusFaultScores = await dispatch(fetchFaultScores(campusIds));

            campusList.forEach(async node => {
                campusFaultScores.forEach(campus => {
                    if (node.id === campus.id) {
                        node.faultScore = _setScore(campus);
                    }
                });

                if (_.isEmpty(node.nodes)) {
                    return;
                }

                const siteIds = node.nodes.map(site => {
                    return site.id;
                });

                const siteFaultScores = await dispatch(fetchFaultScores(siteIds));

                node.nodes.forEach(childNode => {
                    siteFaultScores.forEach(site => {
                        if (childNode.id === site.id) {
                            childNode.faultScore = _setScore(site);
                        }
                    });
                });
            });

            const siteIds = siteList.map(site => {
                return site.id;
            });

            const siteFaultScores = await dispatch(fetchFaultScores(siteIds));

            siteList.forEach(node => {
                siteFaultScores.forEach(site => {
                    if (node.id === site.id) {
                        node.faultScore = _setScore(site);
                    }
                });
            });
        }

        return [...campusList, ...siteList];
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const fetchClientNodes = (companyId, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    dispatch({
        type:    FETCH_CLIENT_NODES,
        payload: []
    });

    try {
        const getPayload = async (page = 1) => {
            options.limit = DEFAULT_PAGE_LIMIT;
            options.sort = DEFAULT_SORT;
            options.page = page;

            const response = await k2aBaseCacheApi.get(
                CompaniesUrl.expand({
                    companyId,
                    ...options
                }),
                header
            );

            const totalCount = _.get(response, 'data.meta.totalCount', 0);
            const payload = _.get(response, 'data.data', []);

            const processedPayload = payload.map(item => _prepareNode(item, 'client'));

            const priorPayload = getState().architect.clientNodes;
            const totalPayload = [...priorPayload, ...processedPayload];

            dispatch({
                type:    FETCH_CLIENT_NODES,
                payload: totalPayload
            });

            page++;

            if (payload.length > 0 && totalPayload.length < totalCount) {
                return await getPayload(page);
            }

            return;
        };

        await getPayload();
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const fetchContacts = (clientId, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    options.limit || (options.limit = DEFAULT_PAGE_LIMIT);

    try {
        const response = await k2aBaseCacheApi.get(
            ContactsUrl.expand({
                clientId,
                ...options
            }),
            header
        );
        const payload = _.get(response, 'data.data', []);

        dispatch({
            type: FETCH_CONTACTS,
            payload
        });
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const fetchFuelTypes = (options = {}) => async (dispatch) => {
    const header = await awsHeader();

    options.limit || (options.limit = DEFAULT_PAGE_LIMIT);

    try {
        const response = await k2aBaseCacheApi.get(
            FuelTypesUrl.expand({
                ...options
            }),
            header
        );

        const payload = _.get(response, 'data.data', []);

        dispatch({
            type: FETCH_FUEL_TYPES,
            payload
        });
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const fetchSite = (siteId, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    try {
        const result = await k2aBaseCacheApi.get(
            SpecificSiteUrl.expand({
                siteId
            }),
            header
        );

        const payload = _.get(result, 'data', []);
        const preparedPayload = _updateNode(payload, 'site');

        const selectedNode = getState().architect.selectedNode;

        if (selectedNode.id === siteId) {
            const updatedSelectedNode = {...selectedNode, ...preparedPayload};

            dispatch(setNode(updatedSelectedNode));
        }

        dispatch(setNodeUpdate(preparedPayload));
    } catch (error) {
        dispatch(handleNetworkError(error));

        return false;
    }
};

export const updateClientChildrenWS = clientId => async (dispatch, getState) => {
    const header = await awsHeader();

    try {
        const result = await k2aBaseCacheApi.get(
            ClientsUrl.expand({
                clientId
            }),
            header
        );

        const payload = _.get(result, 'data', []);
        const preparedPayload = _updateNode(payload, 'client');

        preparedPayload.nodes = await dispatch(fetchCampusAndSiteNodes(clientId));

        dispatch({
            type:    TOGGLE_LOADING,
            payload: false
        });

        dispatch(setNodeUpdate(preparedPayload));
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const updateCompanyChildrenWS = (clientId, companyId, action) => async (dispatch, getState) => {
    const header = await awsHeader();

    const tree = getState().architect.tree;

    const targetCompany = _searchTree(tree, companyId);

    if (action === 'Create') {
        try {
            const result = await k2aBaseCacheApi.get(
                ClientsUrl.expand({
                    clientId
                }),
                header
            );

            const payload = _.get(result, 'data', []);
            const preparedPayload = _updateNode(payload, 'client');

            if (!_.isEmpty(targetCompany)) {
                targetCompany.nodes = [...targetCompany.nodes, preparedPayload].sort(_sorter);

                dispatch(setNodeUpdate(targetCompany));
            }
        } catch (error) {
            dispatch(handleNetworkError(error));
        }
    } else if (action === 'Delete') {
        if (!_.isEmpty(targetCompany)) {
            const updatedNodes = targetCompany.nodes.filter(node => node.id !== clientId);

            targetCompany.nodes = [...updatedNodes];

            dispatch(setNodeUpdate(targetCompany));
        }
    }
};

export const fetchSiteNodes = (clientId, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    options.limit || (options.limit = DEFAULT_PAGE_LIMIT);

    try {
        const response = await k2aBaseCacheApi.get(
            SitesUrl.expand({
                clientId,
                ...options
            }),
            header
        );
        const payload = _.get(response, 'data.data', []);

        const processedPayload = payload.map(item => _prepareNode(item, 'site'));

        return processedPayload;
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const fetchEquipment = (node, site = {}, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();
    let siteId;

    if (_.isObject(node)) {
        siteId = _extractSiteId(node);
    } else {
        siteId = node;
    }

    dispatch({
        type:    FETCH_EQUIPMENT,
        payload: []
    });

    try {
        const getPayload = async (page = 1) => {
            options.limit = DEFAULT_PAGE_LIMIT;
            options.page = page;
            options.extendedData = true;

            const response = await k2aBaseCacheApi.get(
                EquipmentUrl.expand({
                    siteId,
                    ...options
                }),
                header
            );

            const payload = _.get(response, 'data.data', []);
            const totalCount = _.get(response, 'data.meta.totalCount', 0);

            let siteData;
            if (_.isEmpty(site)) {
                siteData = await _fetchSiteData(siteId);
            } else {
                siteData = { ...site };
            }

            let processedPayload;

            if (payload.length) {
                processedPayload = payload.map(item => _prepareChildlessNode(item, siteData, 'equipmentItem'));
            } else {
                processedPayload = [];
            }

            const priorPayload = getState().architect.equipment;
            const totalPayload = [...priorPayload, ...processedPayload];

            dispatch({
                type:    FETCH_EQUIPMENT,
                payload: totalPayload.sort(_sorter)
            });

            page++;

            if (payload.length > 0 && totalPayload.length < totalCount) {
                return await getPayload(page);
            }

            return;
        };

        await getPayload();

    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const fetchEquipmentByAreaId = (node, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    options.AreaId = node.id;
    const siteId = findSiteId(node);

    dispatch({
        type:    FETCH_FILTERED_EQUIPMENT,
        payload: []
    });

    try {
        const getPayload = async (page = 1) => {
            options.limit = DEFAULT_PAGE_LIMIT;
            options.page = page;
            options.extendedData = true;

            const response = await k2aBaseCacheApi.get(
                EquipmentUrl.expand({
                    siteId,
                    ...options
                }),
                header
            );

            const payload = _.get(response, 'data.data', []);
            const totalCount = _.get(response, 'data.meta.totalCount', 0);

            const priorPayload = getState().architect.filteredEquipment;
            const totalPayload = [...priorPayload, ...payload];

            dispatch({
                type:    FETCH_FILTERED_EQUIPMENT,
                payload: payload.sort(_sorter)
            });

            page++;

            if (payload.length > 0 && totalPayload.length < totalCount) {
                return await getPayload(page);
            }

            return;
        };

        await getPayload();

    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const fetchBobEquipmentBySystemId = (siteId, systemId, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    options.SystemId = systemId;

    dispatch({
        type:    FETCH_FILTERED_EQUIPMENT,
        payload: []
    });

    try {
        const getPayload = async (page = 1) => {
            options.limit = DEFAULT_PAGE_LIMIT;
            options.page = page;
            options.extendedData = true;

            const response = await k2aBaseCacheApi.get(
                EquipmentUrl.expand({
                    siteId,
                    ...options
                }),
                header
            );

            const payload = _.get(response, 'data.data', []);
            const totalCount = _.get(response, 'data.meta.totalCount', 0);

            const priorPayload = getState().architect.filteredEquipment;
            const totalPayload = [...priorPayload, ...payload];

            dispatch({
                type:    FETCH_FILTERED_EQUIPMENT,
                payload: totalPayload.sort(_sorter)
            });

            page++;

            if (payload.length > 0 && totalPayload.length < totalCount) {
                return await getPayload(page);
            }

            return;
        };

        await getPayload();

    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const fetchEquipmentBySystemId = (node, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    options.SystemId = node.id;
    const siteId = findSiteId(node);

    dispatch({
        type:    FETCH_FILTERED_EQUIPMENT,
        payload: []
    });

    try {
        const getPayload = async (page = 1) => {
            options.limit = DEFAULT_PAGE_LIMIT;
            options.page = page;
            options.extendedData = true;

            const response = await k2aBaseCacheApi.get(
                EquipmentUrl.expand({
                    siteId,
                    ...options
                }),
                header
            );

            const payload = _.get(response, 'data.data', []);
            const totalCount = _.get(response, 'data.meta.totalCount', 0);

            const priorPayload = getState().architect.filteredEquipment;
            const totalPayload = [...priorPayload, ...payload];

            dispatch({
                type:    FETCH_FILTERED_EQUIPMENT,
                payload: totalPayload.sort(_sorter)
            });

            page++;

            if (payload.length > 0 && totalPayload.length < totalCount) {
                return await getPayload(page);
            }

            return;
        };

        await getPayload();

    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const fetchSiteChildNodes = (node, options = {}) => async (dispatch, getState) => {
    try {
        const siteData = getState().site.selectedSite;
        const treeType = getState().architect.treeType;

        const postalCode = _.get(siteData, 'address.postalCode', '');
        const siteId = _.get(siteData, 'id');

        await Promise.all([
            dispatch(fetchAreas(node.id, siteData)),
            dispatch(fetchEquipment(node.id, siteData)),
            dispatch(fetchSystems(node.id, siteData)),
            dispatch(fetchWeatherStation(postalCode))
        ].map(p => p.catch(err => err)));

        let areaNodes = _.orderBy(getState().architect.areas, ['name'], ['asc']);
        let areaFaultScores = [];
        if (treeType === 'home') {
            const areaIds = areaNodes.map(node => node.id);
            areaFaultScores = await dispatch(fetchFaultScores(areaIds));

            areaNodes.forEach(node => {
                areaFaultScores.forEach(item => {
                    if (node.id === item.id) {
                        node.faultScore = _setScore(item);
                    }
                });
            });
        }

        let equipmentNodes = _.orderBy(getState().architect.equipment, ['name'], ['asc']);
        let equipmentFaultScores = [];
        if (treeType === 'home') {
            const equipmentIds = equipmentNodes.map(node => node.id);
            equipmentFaultScores = await dispatch(fetchFaultScores(equipmentIds));

            equipmentNodes.forEach(node => {
                equipmentFaultScores.forEach(item => {
                    if (node.id === item.id) {
                        node.faultScore = _setScore(item);
                    }
                });
            });
        }

        let systemNodes = _.orderBy(getState().architect.systems, ['name'], ['asc']);
        let systemFaultScores = [];
        if (treeType === 'home') {
            const systemIds = systemNodes.map(node => node.id);
            systemFaultScores = await dispatch(fetchFaultScores(systemIds));

            systemNodes.forEach(node => {
                systemFaultScores.forEach(item => {
                    if (node.id === item.id) {
                        node.faultScore = _setScore(item);
                    }
                });
            });
        }

        const areaId = uuidv4();
        const equipmentId = uuidv4();
        const systemId = uuidv4();

        return [
            {
                faultScore: _detectScore(areaFaultScores),
                nodes:      areaNodes,
                type:       'area',
                key:        areaId,
                label:      'Area',
                id:         areaId,
                siteId
            },
            {
                faultScore: _detectScore(equipmentFaultScores),
                nodes:      equipmentNodes,
                type:       'equipment',
                key:        equipmentId,
                label:      'Equipment',
                id:         equipmentId,
                siteId
            },
            {
                faultScore: _detectScore(systemFaultScores),
                nodes:      systemNodes,
                type:       'system',
                key:        systemId,
                label:      'System',
                id:         systemId,
                siteId
            }
        ];
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const fetchSitePanelData = (node, options = {}) => async (dispatch, getState) => {
    try {
        const siteData = getState().architect.selectedNode;
        const postalCode = _.get(siteData, 'address.postalCode', '');

        await dispatch(fetchAreas(node.id, siteData));
        await dispatch(fetchWeatherStation(postalCode));
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const fetchSeniorityLevels = (options = {}) => async dispatch => {
    const header = await awsHeader();

    try {
        const seniorityResponse = await lambdaBaseCacheApi.get(
            TechnicianSeniorityUrl.expand({
                ...options
            }),
            header
        );

        const seniorityData = _.get(seniorityResponse, 'data.data', []);

        const seniorityLevels = _.orderBy(seniorityData, [seniority => seniority.label.toLowerCase()], ['asc']);

        dispatch({
            type:    FETCH_TECHNICIAN_SENIORITY_LEVELS,
            payload: seniorityLevels
        });
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const fetchSpecialties = (options = {}) => async dispatch => {
    const header = await awsHeader();

    try {
        const specialtiesResponse = await lambdaBaseCacheApi.get(
            TechnicianSpecialityUrl.expand({
                ...options
            }),
            header
        );

        const specialtyData = _.get(specialtiesResponse, 'data.data', []);

        const alphabetizedSpecialties = _.orderBy(specialtyData, [specialty => specialty.label.toLowerCase()], ['asc']);

        const specialties = [
            {
                items:     [...alphabetizedSpecialties],
                name:      'Specialties',
                itemProps: {
                    type: 'specialties'
                }
            }
        ];

        dispatch({
            type:    FETCH_TECHNICIAN_SPECIALTIES,
            payload: specialties
        });
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const fetchSeniorityLevelsandSpecialties = (options = {}) => async dispatch => {
    const header = await awsHeader();

    try {
        const seniorityResponse = await lambdaBaseCacheApi.get(
            TechnicianSeniorityUrl.expand({
                ...options
            }),
            header
        );

        const specialtiesResponse = await lambdaBaseCacheApi.get(
            TechnicianSpecialityUrl.expand({
                ...options
            }),
            header
        );

        const seniorityData = _.get(seniorityResponse, 'data.data', []);
        const specialtyData = _.get(specialtiesResponse, 'data.data', []);

        const alphabetizedSpecialties = _.orderBy(specialtyData, [specialty => specialty.label.toLowerCase()], ['asc']);

        const specialties = [
            {
                items:     [...alphabetizedSpecialties],
                name:      'Specialties',
                itemProps: {
                    type: 'specialties'
                }
            }
        ];

        const seniorityLevels = _.orderBy(seniorityData, [seniority => seniority.label.toLowerCase()], ['asc']);

        batch(() => {
            dispatch({
                type:    FETCH_TECHNICIAN_SENIORITY_LEVELS,
                payload: seniorityLevels
            });

            dispatch({
                type:    FETCH_TECHNICIAN_SPECIALTIES,
                payload: specialties
            });
        });

    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const fetchSensors = (node, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();
    const rawUnits = getState().architect.rawUnits;

    const siteId = findSiteId(node);
    const parentId = node.id;

    dispatch({
        type:    FETCH_SENSORS,
        payload: []
    });

    try {
        const getPayload = async (page = 1) => {
            options.limit = 500;
            options.page = page;

            const response = await k2aBaseCacheApi.get(
                SensorsUrl.expand({
                    siteId,
                    parentId,
                    ...options
                }),
                header
            );

            const payload = _getSensors(response, rawUnits);

            const totalCount = _.get(response, 'data.meta.totalCount', 0);

            const priorPayload = getState().architect.sensors;
            const totalPayload = [...priorPayload, ...payload];

            dispatch({
                type:    FETCH_SENSORS,
                payload: totalPayload
            });

            page++;

            if (payload.length > 0 && totalPayload.length < totalCount) {
                return await getPayload(page);
            }

            return;
        };

        await getPayload();

    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const fetchServiceCallStatuses = (options = {}) => async dispatch => {
    const header = await awsHeader();

    try {
        const response = await lambdaBaseCacheApi.get(
            ServiceCallStatusesUrl.expand({
                ...options
            }),
            header
        );
        const payload = _.get(response, 'data.data', []);

        dispatch({
            type: FETCH_SERVICE_CALL_STATUSES,
            payload
        });
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const fetchServiceAppointmentStatuses = (options = {}) => async dispatch => {
    const header = await awsHeader();

    try {
        const response = await lambdaBaseCacheApi.get(
            ServiceAppointmentStatusesUrl.expand({
                ...options
            }),
            header
        );
        const payload = _.get(response, 'data.data', []);

        dispatch({
            type: FETCH_SERVICE_APPOINTMENT_STATUSES,
            payload
        });
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const fetchSystems = (node, site = {}, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();
    let siteId;

    options.limit || (options.limit = DEFAULT_PAGE_LIMIT);

    if (_.isObject(node)) {
        siteId = _extractSiteId(node);
    } else {
        siteId = node;
    }

    try {
        const response = await k2aBaseCacheApi.get(
            SystemsUrl.expand({
                siteId,
                ...options
            }),
            header
        );

        const payload = _.get(response, 'data.data', []);

        let siteData;
        if (_.isEmpty(site)) {
            siteData = await _fetchSiteData(siteId);
        } else {
            siteData = { ...site };
        }

        let processedPayload;

        if (payload.length) {
            processedPayload = payload.map(item => _prepareChildlessNode(item, siteData, 'systemItem'));
        } else {
            processedPayload = [];
        }

        dispatch({
            type:    FETCH_SYSTEMS,
            payload: processedPayload
        });

    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const fetchBobSystemsByEquipmentId = (siteId, equipmentId, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    options.limit || (options.limit = DEFAULT_PAGE_LIMIT);
    options.equipmentId = equipmentId;

    dispatch({
        type:    FETCH_FILTERED_SYSTEMS,
        payload: []
    });

    try {
        const response = await k2aBaseCacheApi.get(
            SystemsUrl.expand({
                siteId,
                ...options
            }),
            header
        );

        const payload = _.get(response, 'data.data', []);

        dispatch({
            type: FETCH_FILTERED_SYSTEMS,
            payload
        });

    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const fetchSystemsByEquipmentId = (node, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    options.limit || (options.limit = DEFAULT_PAGE_LIMIT);
    options.equipmentId = node.id;
    const siteId = findSiteId(node);
    try {
        const response = await k2aBaseCacheApi.get(
            SystemsUrl.expand({
                siteId,
                ...options
            }),
            header
        );

        const payload = _.get(response, 'data.data', []);

        dispatch({
            type: FETCH_FILTERED_SYSTEMS,
            payload
        });

    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const fetchTechniciansList = (options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();
    const { companyId } = getState().companyInfo;

    const LIMIT = 500;

    try {
        dispatch(beginLoadingForTechnicians());
        options.pagesize = LIMIT;
        options.page = 1;
        options.companyId = companyId;

        const response = await lambdaBaseCacheApi.get(
            TechniciansGetUrl.expand({
                ...options
            }),
            header
        );

        const rawData = _.get(response, 'data.data', []);

        rawData.forEach(tech => {
            tech.name = `${tech.firstName} ${tech.lastName}`;
            tech.type = 'technician';
        });
        const alphabetizedPayload = _.orderBy(rawData, [technician => technician.name.toLowerCase()], ['asc']);

        const payload = [
            {
                items:     [...alphabetizedPayload],
                name:      'Technicians',
                itemProps: {
                    type: 'technicians'
                }
            }
        ];

        await Promise.all([
            dispatch(fetchSeniorityLevels()),
            dispatch(fetchSpecialties())
        ].map(p => p.catch(err => err)));

        dispatch({
            type: FETCH_TECHNICIAN_LIST,
            payload
        });
    } catch (error) {
        dispatch(handleNetworkError(error));
        dispatch(errorLoadingForTechnicians(error));
    } finally {
        dispatch(endLoadingForTechnicians());
    }
};
export const fetchWeatherStation = (zipcode, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    options.zipcode = zipcode;

    try {
        const response = await lambdaBaseCacheApi.get(
            WeatherStationUrl.expand({
                ...options
            }),
            header
        );
        const payload = _.get(response, 'data.data', []);

        dispatch({
            type: FETCH_WEATHER_STATION,
            payload
        });
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const fetchWeatherData = (options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    try {
        dispatch(beginLoading());
        const response = await lambdaBaseCacheApi.get(
            WeatherDataUrl.expand({
                ...options
            }),
            header
        );
        const payload = _.get(response, 'data.data', []);

        dispatch({
            type: FETCH_WEATHER_DATA,
            payload
        });
    } catch (error) {
        dispatch(errorLoading(getErrorMessage(error)));
        dispatch(handleNetworkError(error));
    } finally {
        dispatch(endLoading());
    }
};

const beginLoading = () => ({
    type:    WEATHER_DATA_LOAD_BEGIN,
    payload: null
});

const endLoading = () => ({
    type:    WEATHER_DATA_LOAD_END,
    payload: null
});

const errorLoading = (error) => ({
    type:    WEATHER_DATA_LOAD_ERROR,
    payload: error
});

const beginLoadingForTechnicians = () => ({
    type:    TECHNICIAN_DATA_LOAD_BEGIN,
    payload: null
});

const endLoadingForTechnicians = () => ({
    type:    TECHNICIAN_DATA_LOAD_END,
    payload: null
});

const errorLoadingForTechnicians = (error) => ({
    type:    TECHNICIAN_DATA_LOAD_ERROR,
    payload: error
});

export const clearWeatherData = () => ({
    type:    FETCH_WEATHER_DATA,
    payload: []
});

export const clearErpEntities = () => ({
    type:    CLEAR_ERP_ENTITIES,
    payload: []
});

export const clearContacts = () => (dispatch, getState) => {
    dispatch({
        type: CLEAR_CONTACTS
    });
};

export const moveSite = (siteId, clientId, body, companyInfo) => async dispatch => {
    const header = await awsHeader();

    try {
        await dispatch(updateSite(siteId, body));

        await k2aBaseCacheApi.post(
            ReassignSiteUrl.expand({
                siteId,
                clientId
            }),
            {},
            header
        );

        await dispatch(initializeTree(companyInfo));

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));

        return false;
    }
};

export const reorderItems = (newOrderBy) => async (dispatch, getState) => {
    const currentState = getState().architect;
    const order = getSortOrder(newOrderBy, currentState);

    dispatch({
        type:    ITEM_REORDER,
        payload: {
            order,
            orderBy: newOrderBy
        }
    });
};

export const setClientNode = (clientId, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    try {
        const response = await k2aBaseCacheApi.get(
            ClientsUrl.expand({
                clientId,
                ...options
            }),
            header
        );

        const payload = _.get(response, 'data', {});

        const processedPayload = _prepareNode(payload, 'client');

        dispatch(setNode(processedPayload));
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const setArea = payload => ({
    type: SET_AREA,
    payload
});

export const setContact = payload => ({
    type: SET_CONTACT,
    payload
});

export const setConversion = payload => ({
    type: SET_CONVERSION,
    payload
});

export const setEquipment = payload => ({
    type: SET_EQUIPMENT,
    payload
});

export const setErpAgent = payload => ({
    type: SET_ERP_AGENT,
    payload
});

export const setLink = payload => ({
    type: SET_LINK,
    payload
});

export const setSensor = payload => ({
    type: SET_SENSOR,
    payload
});

export const setSystem = payload => ({
    type: SET_SYSTEM,
    payload
});

export const setNode = payload => ({
    type: SET_NODE,
    payload
});

export const setNodeUpdate = payload => ({
    type: SET_NODE_UPDATE,
    payload
});

export const saveTreeReset = payload => ({
    type: SET_TREE_RESET,
    payload
});

export const setSiteNode = site => async (dispatch, getState) => {
    const header = await awsHeader();
    const { id: siteId } = site;

    try {
        const response = await k2aBaseCacheApi.get(
            SpecificSiteUrl.expand({
                siteId
            }),
            header
        );

        const payload = _.get(response, 'data', {});

        const updatedSite = Object.assign({}, payload, site);
        updatedSite.key = updatedSite.id;

        dispatch(setNode(updatedSite));
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const setEquipmentNode = equipment => async dispatch => {
    const header = await awsHeader();
    const { id: equipmentId } = equipment;

    try {
        const response = await k2aBaseCacheApi.get(
            SpecificEquipmentUrl.expand({
                equipmentId
            }),
            header
        );

        const updatedEquipment = _preparePayload(response, equipment);

        dispatch(setNode(updatedEquipment));
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const setAreaNode = area => async dispatch => {
    const header = await awsHeader();
    const { id: areaId } = area;

    try {
        const response = await k2aBaseCacheApi.get(
            SpecificAreaUrl.expand({
                areaId
            }),
            header
        );

        const updatedArea = _preparePayload(response, area);

        dispatch(setNode(updatedArea));
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const setSelectedTechnician = selectedTechnician => ({
    type: SET_SELECTED_TECHNICIAN,
    selectedTechnician
});

export const setSystemNode = system => async dispatch => {
    const header = await awsHeader();
    const { id: systemId } = system;

    try {
        const response = await k2aBaseCacheApi.get(
            SpecificSystemUrl.expand({
                systemId
            }),
            header
        );

        const updatedSystem = _preparePayload(response, system);

        dispatch(setNode(updatedSystem));
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const setTree = payload => ({
    type: SET_TREE,
    payload
});

export const setTreeType = payload => ({
    type: SET_TREE_TYPE,
    payload
});

export const toggleContactsLoading = isLoading => ({
    type:    TOGGLE_CONTACT_LINKS_LOADING,
    payload: isLoading
});

export const toggleErpClientsLoading = isLoading => ({
    type:    TOGGLE_ERP_CLIENTS_LOADING,
    payload: isLoading
});

export const updateArea = (areaId, companyInfo, body, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    try {
        await k2aBaseCacheApi.put(
            SpecificAreaUrl.expand({
                areaId,
                ...options
            }),
            body,
            header
        );

        const result = await k2aBaseCacheApi.get(
            SpecificAreaUrl.expand({
                areaId
            }),
            header
        );

        const payload = _.get(result, 'data', []);
        const preparedPayload = _updateNode(payload, 'areaItem');

        dispatch(enqueueSnackbar('Area updated successfully.', 'success'));
        dispatch(setNodeUpdate(preparedPayload));

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));

        return false;
    }
};

export const updateBucketNode = (siteId, targetId, bucket, action) => async (dispatch, getState) => {
    let bucketData;
    const tree = getState().architect.tree;

    const targetSite = _searchTree(tree, siteId);

    if (action === 'Create' || action === 'Update') {
        switch (bucket) {
            case 'area':
                bucketData = _.orderBy(getState().architect.areas, ['name'], ['asc']);
                break;
            case 'equipment':
                bucketData = _.orderBy(getState().architect.equipment, ['name'], ['asc']);
                break;
            case 'system':
                bucketData = _.orderBy(getState().architect.systems, ['name'], ['asc']);
                break;
            default:
                break;
        }

        if (!_.isEmpty(targetSite) && !_.isEmpty(targetSite.nodes)) {
            targetSite.nodes.forEach(node => {
                if (node.type === bucket) {
                    node.nodes = [...bucketData];
                }
            });
        }

    } else if (action === 'Delete') {
        let bucketNodes;

        if (!_.isEmpty(targetSite) && !_.isEmpty(targetSite.nodes)) {
            targetSite.nodes.forEach(node => {
                if (node.type === bucket) {
                    bucketNodes = node.nodes.filter(n => n.id !== targetId);

                    node.nodes = [...bucketNodes];
                }
            });
        }

        switch (bucket) {
            case 'equipment':
                dispatch({
                    type:    FETCH_EQUIPMENT,
                    payload: bucketNodes
                });
                break;
            default:
                break;
        }
    }

    dispatch(setNodeUpdate(targetSite));
};

export const updateCampus = (campusId, clientId, companyInfo, body, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    try {
        await k2aBaseCacheApi.put(
            CampusUrl.expand({
                campusId,
                ...options
            }),
            body,
            header
        );

        const result = await k2aBaseCacheApi.get(
            CampusUrl.expand({
                campusId
            }),
            header
        );

        const payload = _.get(result, 'data', []);
        const preparedPayload = _updateNode(payload, 'campus');

        dispatch(enqueueSnackbar('Campus updated successfully.', 'success'));
        dispatch(setNodeUpdate(preparedPayload));

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));

        return false;
    }
};

export const updateClient = (companyInfo, clientId, body, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    try {
        await k2aBaseCacheApi.put(
            ClientsUrl.expand({
                clientId,
                ...options
            }),
            body,
            header
        );

        dispatch(enqueueSnackbar('Client updated successfully.', 'success'));

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));

        return false;
    }
};

export const updateContact = (contactId, clientId, body, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    try {
        await k2aBaseCacheApi.put(
            SpecificContactUrl.expand({
                contactId,
                ...options
            }),
            body,
            header
        );

        dispatch(setClientNode(clientId));

        dispatch(enqueueSnackbar('Contact updated successfully.', 'success'));

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));

        return false;
    }
};

export const updateConversion = (conversionId, companyInfo, body, options = {}) => async (dispatch, getState) => {
    const { companyId } = companyInfo;
    const header = await awsHeader();

    try {
        await k2aBaseCacheApi.put(
            SpecificConversionUrl.expand({
                conversionId,
                ...options
            }),
            body,
            header
        );

        await dispatch(fetchConversions(companyId));
        dispatch(enqueueSnackbar('Conversion updated successfully.', 'success'));

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));

        return false;
    }
};

export const updateEquipment = (equipmentId, siteName, body, options = {}) => async (dispatch) => {
    const header = await awsHeader();

    try {
        await k2aBaseCacheApi.put(
            SpecificEquipmentUrl.expand({
                equipmentId,
                ...options
            }),
            body,
            header
        );

        options.extendedData = true;

        const result = await k2aBaseCacheApi.get(
            SpecificEquipmentUrl.expand({
                equipmentId,
                ...options
            }),
            header
        );

        const payload = _.get(result, 'data', []);
        if (!payload.manufacturer) {
            payload.manufacturer = {
                id:   defaultManufacturer,
                name: null
            };
        }

        payload.siteName = siteName;

        const preparedPayload = _updateNode(payload, 'equipmentItem');

        dispatch(enqueueSnackbar('Equipment updated successfully.', 'success'));
        dispatch(setNodeUpdate(preparedPayload));
        dispatch(setNode(preparedPayload));

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));

        return false;
    }
};

export const updateErpServiceCallMapping = (primaryKey, entityId, externalId, agentConfigurationId, erpEntityKind = 'status') => async(dispatch) => {
    const header = await awsHeader();
    const body = {
        id:                   primaryKey,
        erpEntityKind:        erpEntityKind,
        entityId:             entityId,
        externalId:           externalId,
        agentConfigurationId: agentConfigurationId
    };

    try {
        await lambdaBaseCacheApi.put(
            EntityMappingsPostUrl.expand({}),
            body,
            header
        );

        await dispatch(fetchErpServiceCallMapping(agentConfigurationId));

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));
        return false;
    }
};

export const updateSite = (siteId, body, options = {}) => async (dispatch) => {
    const header = await awsHeader();

    try {
        await k2aBaseCacheApi.put(
            SpecificSiteUrl.expand({
                siteId,
                ...options
            }),
            body,
            header
        );

        await dispatch(fetchSite(siteId));
        dispatch(enqueueSnackbar('Site updated successfully.', 'success'));

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));

        return false;
    }
};

export const updateSensor = (sensorId, parentId, siteId, companyInfo, body, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();
    const rawUnits = getState().architect.rawUnits;

    try {
        await k2aBaseCacheApi.put(
            SpecificSensorUrl.expand({
                sensorId,
                ...options
            }),
            body,
            header
        );

        options.limit = DEFAULT_PAGE_LIMIT;
        options.page = 1;
        const response = await k2aBaseCacheApi.get(
            SensorsUrl.expand({
                siteId,
                parentId,
                ...options
            }),
            header
        );

        const payload = _getSensors(response, rawUnits);

        dispatch({
            type: FETCH_SENSORS,
            payload
        });

        dispatch(enqueueSnackbar('Sensor updated successfully.', 'success'));

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));

        return false;
    }
};

export const updateSystem = (systemId, companyInfo, body, options = {}) => async (dispatch) => {
    const header = await awsHeader();

    try {
        await k2aBaseCacheApi.put(
            SpecificSystemUrl.expand({
                systemId,
                ...options
            }),
            body,
            header
        );

        const result = await k2aBaseCacheApi.get(
            SpecificSystemUrl.expand({
                systemId
            }),
            header
        );

        const payload = _.get(result, 'data', []);
        const preparedPayload = _updateNode(payload, 'systemItem');

        dispatch(enqueueSnackbar('System updated successfully.', 'success'));
        dispatch(setNodeUpdate(preparedPayload));

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));

        return false;
    }
};

export const updateTechnician = (technician, options = {}) => async (dispatch, getState) => {
    const headers = await awsHeader();
    const { companyId } = getState().companyInfo;

    try {
        await lambdaBaseCacheApi.patch(
            TechniciansUrl.expand(),
            {
                ...technician,
                companyId
            },
            headers
        );

        if (technician.imageImport) await doFileImport(technician.imageImport, technician.id, headers);

        dispatch(enqueueSnackbar('Technician updated successfully.', 'success'));
        dispatch(fetchTechniciansList());

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));

        return false;
    }
};

export const createTechnician = (technician) => async (dispatch, getState) => {
    const headers = await awsHeader();
    const { companyId } = getState().companyInfo;

    try {
        const response = await lambdaBaseCacheApi.post(
            TechniciansUrl.expand(),
            {
                ...technician,
                companyId
            },
            headers
        );

        const technicianId = _.get(response, 'data.data.id', '');

        if (technician.imageImport) {
            await doFileImport(technician.imageImport, technicianId, headers);

            //Because of the api relationship of:
            // api1 new technician created -> api2 new photo + url created -> api2 sends api1 photo url
            //if we simply request the technicians immediately after creation, it won't have the photo url
            //to solve this, below is a delay of 10 seconds, which is the upper bound estimation of how long
            //that process takes. Future developers may want to consider implementing web sockets as a cleaner
            //solution to this chicken + egg problem.
            dispatch(beginLoadingForTechnicians());
            setTimeout(async ()=>{
                dispatch(enqueueSnackbar('Technician created successfully', 'success'));
                await dispatch(fetchTechniciansList());
                let newTechnician = [];
                newTechnician = getState().architect.techniciansList[0].items.filter(tech => tech.id === technicianId).pop();
                dispatch(setSelectedTechnician({ ...newTechnician, id: technicianId }));
            }, 10000);
        }
        else {
            dispatch(enqueueSnackbar('Technician created successfully', 'success'));
            await dispatch(fetchTechniciansList());
            dispatch(setSelectedTechnician({ ...technician, id: technicianId }));
        }

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));
        return false;
    }
};

export const deleteTechnician = (technicianId) => async (dispatch, getState) => {
    const header = await awsHeader();

    const { companyId } = getState().companyInfo;

    try {
        await lambdaBaseCacheApi.delete(
            TechniciansDeleteUrl.expand({
                technicianId,
                companyId
            }),
            header
        );

        dispatch(enqueueSnackbar('Technician deleted successfully', 'success'));

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));
        return false;
    }
};

const _updateNode = (node, type) => {
    return {
        type,
        key:   node.id,
        label: node.name,
        ...node
    };
};

const _prepareNode = (node, type) => {
    return {
        nodes: [{ 'label': 'Loading...', 'key': Math.floor(Math.random() * Math.floor(100000)) }],
        type,
        key:   node.id,
        label: node.name,
        ...node
    };
};

const _prepareNodes = (payload) => {
    let clientNodes = [];

    payload.forEach(item => {
        const node = {
            nodes:          [],
            type:           'client',
            key:            item.client.id,
            label:          item.client.name,
            id:             item.client.id,
            name:           item.client.name,
            address:        item.client.address,
            businessType:   item.client.businessType,
            primaryContact: item.client.primaryContact,
            faultScore:     item.client.faultScore || ''
        };

        item.campusSites.campusList.forEach(campus => {
            const campusNode = {
                nodes: [],
                type:  'campus',
                key:   campus.id,
                label: campus.name,
                ...campus
            };

            campus.siteList.forEach(site => {
                const siteNode = {
                    nodes: [{ 'label': 'Loading...', 'key': Math.floor(Math.random() * Math.floor(100000)) }],
                    type:  'site',
                    key:   site.id,
                    label: site.name,
                    ...site
                };
                campusNode.nodes.push(siteNode);
            });

            node.nodes.push(campusNode);
        });

        item.campusSites.siteList.forEach(site => {
            const siteNode = {
                nodes: [{ 'label': 'Loading...', 'key': Math.floor(Math.random() * Math.floor(100000)) }],
                type:  'site',
                key:   site.id,
                label: site.name,
                ...site
            };

            node.nodes.push(siteNode);
        });

        clientNodes.push(node);
    });

    clientNodes.forEach(client => {
        client.nodes.sort((a, b) => _sorter(a, b));
    });

    return clientNodes;
};

const _prepareChildlessNode = (node, siteData, type) => {
    return {
        nodes:    [],
        type,
        key:      node.id,
        label:    node.name,
        siteName: siteData.name,
        ...node
    };
};

const _getDisplayUnit = (presentationUnitId, rawUnits) => {
    let match = '';

    rawUnits.forEach(unit => {
        unit.items.forEach(item => {
            if (item.id === presentationUnitId) {
                return match = item.name;
            }
        });
    });

    return match;
};

const _getSensors = (response, rawUnits) => {
    return _.chain(response).get('data.data', []).map(sensor => {
        return {
            ...sensor,
            displayUnit: _getDisplayUnit(sensor.presentationUnitId, rawUnits)
        };
    }).value();
};

const _extractSiteId = node => {
    return node.parent.split('/').pop();
};

const _preparePayload = (response, node) => {
    const payload = _.get(response, 'data', {});

    const updatedPayload = Object.assign({}, payload, node);
    updatedPayload.key = updatedPayload.id;

    return updatedPayload;
};

const _fetchSiteData = async siteId => {
    const header = await awsHeader();

    try {
        const response = await k2aBaseCacheApi.get(
            SpecificSiteUrl.expand({
                siteId
            }),
            header
        );

        return _.get(response, 'data', []);
    } catch (error) {
        // eslint-disable-next-line no-console
        console.log('error caught: ', error);
    }
};

const _searchTree = (tree, id) => {
    let targetNode = {};

    const search = (tree, id) => {
        return tree.reduce((r, e) => {
            if (e.id === id) {
                targetNode = { ...e };
            } else {
                if (e.nodes) search(e.nodes, id);
            }

            return r;
        }, []);
    };

    search(tree, id);

    return targetNode;
};

const _removeNode = (tree, id) => {
    const treeCopy = [...tree];

    const _remove = (t, id) => {
        return t.reduce((r, e) => {
            if (e.nodes) {
                e.nodes = e.nodes.filter(node => node.id !== id);

                _remove(e.nodes, id);
            }

            return r;
        }, []);
    };

    _remove(treeCopy, id);

    return treeCopy;
};

const _sorter = (a, b) => {
    let roleA = a.name.toUpperCase();
    let roleB = b.name.toUpperCase();

    if (roleA < roleB) {
        return -1;
    }
    if (roleA > roleB) {
        return 1;
    }

    return 0;
};

const _setScore = node => {
    let score = 'low';
    if (Number(node.high)) {
        score = 'high';
    } else if (Number(node.medium)) {
        score = 'medium';
    }

    return score;
};

const _detectScore = scores => {
    if (_.isEmpty(scores)) return 'low';

    const highFound = scores.some(score => score.high > 0);
    const mediumFound = scores.some(score => score.medium > 0);

    if (highFound) return 'high';
    else if (mediumFound) return 'medium';
    else return 'low';
};
